import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { IndividualCustomer } from '@/types/customer'
import { returnCasesHttp } from '@/services/returnCases'
import { toast } from '@/store/toastStore'
import type { CaseParticipantAction, CaseParticipantKind } from '@/types/returnCase.types'

const ACTIONS: CaseParticipantAction[] = [
  'VIEW_CASE',
  'ANSWER_APPLICABILITY',
  'UPLOAD_DOCUMENT',
  'ENTER_FACT',
  'RESPOND_QUERY',
  'CONFIRM_SECTION',
]

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export default function CaseParticipantPanel({
  caseId,
  customer,
}: {
  caseId: string
  customer?: IndividualCustomer
}) {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: ['case-participants', caseId],
    queryFn: () => returnCasesHttp.participants(caseId),
  })
  const [userId, setUserId] = useState('')
  const [email, setEmail] = useState('')
  const [kind, setKind] = useState<CaseParticipantKind>('PRINCIPAL')
  const [representativeGrantId, setRepresentativeGrantId] = useState('')
  const [actions, setActions] = useState<CaseParticipantAction[]>([
    'VIEW_CASE',
    'ANSWER_APPLICABILITY',
  ])
  const [deliveryToken, setDeliveryToken] = useState<string | null>(null)
  const [revoking, setRevoking] = useState<{ inviteId: string; label: string } | null>(null)
  const [revocationReason, setRevocationReason] = useState('')
  const [inviteError, setInviteError] = useState('')

  useEffect(() => {
    setUserId(kind === 'PRINCIPAL' ? (customer?.ownerUserId ?? '') : '')
    setEmail(kind === 'PRINCIPAL' ? (customer?.emailPrimary ?? '') : '')
    setInviteError('')
  }, [customer?.id, customer?.ownerUserId, kind])

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['case-participants', caseId] })
  const issue = useMutation({
    mutationFn: () =>
      returnCasesHttp.issueParticipantInvite(caseId, {
        intended_user_id: userId.trim(),
        intended_email: email.trim(),
        participant_kind: kind,
        purpose: 'RETURN_PREPARATION',
        actions,
        representative_grant_id:
          kind === 'REPRESENTATIVE' ? representativeGrantId.trim() : undefined,
      }),
    onSuccess: async participant => {
      setDeliveryToken(participant.delivery_token ?? null)
      await refresh()
      toast.success('Case invitation issued', 'The one-time delivery link is ready.')
    },
    onError: error => {
      setInviteError(error instanceof Error ? error.message : 'Check the participant scope.')
      toast.error(
        'Invitation rejected',
        error instanceof Error ? error.message : 'Check the participant scope.'
      )
    },
  })
  const revoke = useMutation({
    mutationFn: ({ inviteId, reason }: { inviteId: string; reason: string }) =>
      returnCasesHttp.revokeParticipantInvite(caseId, inviteId, reason),
    onSuccess: async () => {
      setRevoking(null)
      setRevocationReason('')
      await refresh()
      toast.success('Participant access revoked')
    },
    onError: error =>
      toast.error('Revocation rejected', error instanceof Error ? error.message : 'Please retry.'),
  })
  const resend = useMutation({
    mutationFn: (inviteId: string) => returnCasesHttp.resendParticipantInvite(caseId, inviteId),
    onSuccess: async participant => {
      setDeliveryToken(participant.delivery_token ?? null)
      await refresh()
      toast.success('Replacement invitation issued')
    },
    onError: error =>
      toast.error(
        'Replacement rejected',
        error instanceof Error ? error.message : 'Only pending invitations can be replaced.'
      ),
  })

  const toggle = (action: CaseParticipantAction) =>
    setActions(current =>
      current.includes(action) ? current.filter(item => item !== action) : [...current, action]
    )
  const deliveryLink = deliveryToken
    ? `${window.location.origin}/case-invite?token=${encodeURIComponent(deliveryToken)}`
    : null

  return (
    <section className="card mt-4 space-y-4 p-4" aria-labelledby="case-participants-heading">
      <div>
        <h2 id="case-participants-heading" className="font-semibold">
          Customer participation
        </h2>
        <p className="text-xs text-text-secondary">
          Invitations are single-use, identity-verified, case-bound, and time-limited.
        </p>
      </div>
      {inviteError && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {inviteError}
        </p>
      )}
      <p className="text-sm text-text-secondary">
        The customer's linked client account is prefilled when available. Use the verified account
        UUID and profile email for a representative.
      </p>
      <form
        className="grid gap-4 md:grid-cols-2"
        onSubmit={event => {
          event.preventDefault()
          if (!UUID_PATTERN.test(userId.trim())) {
            setInviteError('Use the participant account UUID. A customer number is not a user ID.')
            return
          }
          if (kind === 'REPRESENTATIVE' && !UUID_PATTERN.test(representativeGrantId.trim())) {
            setInviteError('Use a valid representative grant UUID.')
            return
          }
          setInviteError('')
          issue.mutate()
        }}
      >
        <label className="text-sm">
          Participant type
          <select
            className="input mt-1 w-full"
            value={kind}
            onChange={event => {
              const next = event.target.value as CaseParticipantKind
              setKind(next)
              if (next === 'REPRESENTATIVE')
                setActions(current => current.filter(action => action !== 'CONFIRM_SECTION'))
            }}
          >
            <option value="PRINCIPAL">Individual client</option>
            <option value="REPRESENTATIVE">Authorized representative</option>
          </select>
        </label>
        <label className="text-sm">
          Verified user ID
          <input
            className="input mt-1 w-full"
            required
            value={userId}
            onChange={event => setUserId(event.target.value)}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            aria-describedby="participant-account-help"
            autoComplete="off"
          />
        </label>
        <p id="participant-account-help" className="text-sm text-text-secondary md:col-span-2">
          Client account UUID, available in Team user details. Customer IDs such as 1 cannot be used
          here.
        </p>
        <label className="text-sm">
          Profile email
          <input
            className="input mt-1 w-full"
            required
            type="email"
            value={email}
            onChange={event => setEmail(event.target.value)}
            autoComplete="off"
          />
        </label>
        {kind === 'REPRESENTATIVE' && (
          <label className="text-sm">
            Representative grant ID
            <input
              className="input mt-1 w-full"
              required
              value={representativeGrantId}
              onChange={event => setRepresentativeGrantId(event.target.value)}
              autoComplete="off"
            />
          </label>
        )}
        <fieldset className="md:col-span-2">
          <legend className="text-sm">Allowed actions</legend>
          <div className="mt-2 flex flex-wrap gap-3">
            {ACTIONS.filter(action => kind === 'PRINCIPAL' || action !== 'CONFIRM_SECTION').map(
              action => (
                <label key={action} className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={actions.includes(action)}
                    onChange={() => toggle(action)}
                  />
                  {action.replace(/_/g, ' ')}
                </label>
              )
            )}
          </div>
        </fieldset>
        <button
          className="btn btn-primary w-fit"
          disabled={issue.isPending || actions.length === 0}
        >
          Issue invitation
        </button>
      </form>
      {deliveryLink && (
        <div className="alert alert-info" role="status">
          <strong>Copy now:</strong> this replacement-safe link is shown once.
          <div className="mt-2 break-all font-mono text-xs">{deliveryLink}</div>
          <button
            type="button"
            className="btn btn-secondary mt-2"
            onClick={() => navigator.clipboard.writeText(deliveryLink)}
          >
            Copy invitation link
          </button>
        </div>
      )}
      {query.isLoading && <p className="text-sm text-text-secondary">Loading participants…</p>}
      {query.isError && (
        <div className="alert alert-error" role="alert">
          Participant metadata could not be loaded.
        </div>
      )}
      <ul className="space-y-2">
        {query.data?.map(item => (
          <li key={item.id} className="rounded border border-border p-3 text-sm">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <strong>
                  {item.participant_kind === 'PRINCIPAL'
                    ? 'Individual client'
                    : 'Authorized representative'}
                </strong>
                <div>
                  {item.masked_email} · {item.status}
                </div>
                <div className="text-xs text-text-secondary">
                  {item.actions.join(', ')} · expires {new Date(item.expires_at).toLocaleString()}
                </div>
              </div>
              <div className="flex gap-2">
                {item.status === 'INVITED' && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={resend.isPending}
                    onClick={() => resend.mutate(item.invite_id)}
                  >
                    Replace invite
                  </button>
                )}
                {!['REVOKED', 'EXPIRED'].includes(item.status) && (
                  <button
                    type="button"
                    className="btn btn-danger"
                    disabled={revoke.isPending}
                    onClick={() => {
                      setRevoking({ inviteId: item.invite_id, label: item.masked_email })
                      setRevocationReason('')
                    }}
                  >
                    Revoke
                  </button>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
      {revoking && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="revoke-participant-heading"
        >
          <form
            className="card w-full max-w-md space-y-4 p-5"
            onSubmit={event => {
              event.preventDefault()
              revoke.mutate({ inviteId: revoking.inviteId, reason: revocationReason.trim() })
            }}
          >
            <div>
              <h3 id="revoke-participant-heading" className="font-semibold">
                Revoke participant access
              </h3>
              <p className="mt-1 text-sm text-text-secondary">
                Access for {revoking.label} will stop immediately.
              </p>
            </div>
            <label className="block text-sm">
              Reason
              <textarea
                className="input mt-1 min-h-24 w-full"
                autoFocus
                required
                minLength={3}
                value={revocationReason}
                onChange={event => setRevocationReason(event.target.value)}
              />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn btn-secondary" onClick={() => setRevoking(null)}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-danger"
                disabled={revoke.isPending || revocationReason.trim().length < 3}
              >
                Revoke access
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  )
}
